import React, { PropsWithChildren } from 'react'
import { useTranslation } from 'react-i18next'

import { useModal } from '@context/ModalsContext'

import { Body1, Caption1 } from '@ui/Typography'
import { ActionIcon } from '@ui/components/ActionIcon'
import { Badge } from '@ui/components/Badge/Badge'
import { Checkbox } from '@ui/components/Checkbox'
import { InfoIcon } from '@ui/icons/Info.icon'

import { useWindowResize } from '@utils/domUtils'

import { RegulatorProductOption } from '@model/RegulatorOptionDto'
import { TickmillProductType } from '@model/TickmillProductType'

import { Box } from '@shared/components/Box'
import { InformationModal } from '@shared/modals/Information.modal'

import styles from '../Pages/PersonalDetails/PersonalDetailsPage.module.scss'

export interface ProductFieldProps {
  tickmillProduct: RegulatorProductOption
  disabled?: boolean
  minDeposit: string
  productIds: TickmillProductType[]
  onChangeProductIds(value: boolean, tickmillProductType: TickmillProductType): void
}

export const ProductField: React.FC<PropsWithChildren<ProductFieldProps>> = (props) => {
  const {
    tickmillProduct,
    disabled = false,
    onChangeProductIds,
    minDeposit,
    children,
    productIds,
  } = props

  const { t } = useTranslation()

  const isChecked = productIds.includes(tickmillProduct.id)

  const isMobile = useWindowResize()

  const infoModal = useModal(InformationModal, {
    title: t('Sign up.General Information'),
    list: tickmillProduct.optionPopup,
  })

  const isProductLogoAvailable = Boolean(
    tickmillProduct.logoUrl && tickmillProduct.logoUrl.length > 0
  )

  return (
    <Box flex className={styles.itemContainer}>
      <Box
        flex
        direction={isMobile ? 'column' : 'row'}
        gap='x16'
        align={isMobile ? 'flex-start' : 'center'}
      >
        <Checkbox
          name='productIds'
          checked={isChecked}
          disabled={disabled}
          onChange={(value) => onChangeProductIds(value, tickmillProduct.id)}
        >
          <Body1 className={styles.productText} color='text-high-em'>
            {children}
          </Body1>
        </Checkbox>
        <Box flex align='center' gap='x8'>
          <Badge size='sm' type='black' contrast='high'>
            <Box flex align='center' gap='x4'>
              <Caption1 medium wrap='wrap'>
                {minDeposit}
              </Caption1>
              {isProductLogoAvailable && (
                <img
                  src={tickmillProduct.logoUrl}
                  alt={tickmillProduct.name}
                  className={styles.productLogo}
                />
              )}
            </Box>
          </Badge>

          {tickmillProduct.optionPopup && tickmillProduct.optionPopup.length > 0 && (
            <ActionIcon
              icon={InfoIcon}
              size='x20'
              onClick={() => infoModal.open()}
              aria-label={t('Sign up.General Information')}
            />
          )}
        </Box>
      </Box>
    </Box>
  )
}
